/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import {
  AuthResult,
  LoginCodeStatusTypes,
  PaymentPeriod,
  UserConsentsState,
  UserSubscriptionStatus,
} from "../../../../enums";
import { HTTP_RESPONSE_TYPE } from "@nf/constants";
import {
  IAuthResponseModel,
  IErrorModel as ICommonErrorModel,
  IPaymentDetailsModel,
  IPaymentInvoiceModel,
  IPaymentOfferModel,
  IPaymentOrderModel,
  IUserConsentModel as ICommonUserConsentModel,
  IUserInfoModel,
  IUserModel,
  IUserSubscriptionModel,
  IAuthSignUpResponseModel,
  IValidCouponModel,
  IOrderStatusModel,
  ILocaleModel as IInternalLocaleModel,
  IProductModel,
} from "../../../../models";
import {
  IConsentListModel,
  IConsentModel,
  ICustomerModel,
  ILocaleModel,
  ILoginResponseModel,
  IOfferResponseModel,
  IOfferSwitchModel,
  IOfferSwitchResponseModel,
  IOrderStatusResponseModel,
  IPaymentDetailsResponseModel,
  IPaymentInvoiceResponseModel,
  IPaymentOrderResponseModel,
  IProductResponseModel,
  ISignupResponseModel,
  ISubscriptionModel,
  IUserConsentModel,
  IValidateCouponResponseModel,
} from "../models";
import { TimeHelper } from "../../../../helpers";

export class ModelsMapperHelper {
  static toAuthResponseModel(data: ILoginResponseModel): IAuthResponseModel {
    return {
      AuthorizationToken: {
        AuthResult: AuthResult.OK,
        Token: data.idToken,
        RefreshToken: data.refreshToken,
        DataToken: data.ottToken,
      },
    };
  }

  static toAuthSignUpResponseModel(
    data: ISignupResponseModel
  ): IAuthSignUpResponseModel {
    return {
      Message: data.message,
    };
  }

  static toUserInfoModel(data: ICustomerModel): IUserInfoModel {
    return {
      Id: data.id,
      UserName: data.email,
      FullName: `${data.first_name} ${data.last_name}`,
      Email: data.email,
      PhoneNumber: data.phone ?? "",
      Locale: data.locale,
      UtmSource: data.utm_source,
      UtmMedium: data.utm_medium,
      UtmCampaign: data.utm_campaign,
    };
  }

  static toUserModel(data: ICustomerModel): IUserModel {
    const result: IUserModel = {
      Id: data.id,
      FullName: `${data.first_name} ${data.last_name}`,
      FirstName: data.first_name,
      LastName: data.last_name,
      PhoneNumber: data.phone ?? "",
      PhoneNumberPrefix: data.phone_prefix ?? "",
      PhoneNumberNumber: data.phone_number ?? "",
    };

    if (data?.email) result.Email = data.email;

    return result;
  }

  static toErrorModel(error: any): ICommonErrorModel {
    if (error === LoginCodeStatusTypes.expired)
      return {
        Message: "web_token_expired",
        ResultType: HTTP_RESPONSE_TYPE.ERROR,
        Data: error?.data,
      };

    //disabled because of usage in server side
    // if (DOMException) {
    //   if (
    //     (error instanceof DOMException && error.code === 22) ||
    //     error.code === 1014 ||
    //     error.name === "QuotaExceededError" ||
    //     error.name === "NS_ERROR_DOM_QUOTA_REACHED"
    //   ) {
    //     return {
    //       Message: "Login failed. Please clean cache",
    //       ResultType: HTTP_RESPONSE_TYPE.ERROR,
    //     };
    //   }
    // }

    if (error?.message) {
      return {
        Message: error.message,
        ResultType: HTTP_RESPONSE_TYPE.ERROR,
        Data: error?.data,
      };
    } else if (error.errors?.[0]?.message) {
      return {
        Message: error?.errors?.[0]?.message,
        ResultType: HTTP_RESPONSE_TYPE.ERROR,
        Data: error?.data,
      };
    } else {
      return {
        Message: error?.errors?.[0]?.message,
        ResultType: HTTP_RESPONSE_TYPE.ERROR,
        Data: error?.data,
      };
    }
  }

  static toConsentModel(
    response: [
      IConsentListModel<IConsentModel[]>,
      IConsentListModel<IUserConsentModel[]>
    ],
    locale: string
  ) {
    const userLocale = locale.split("_")[1];

    const consents: ICommonUserConsentModel[] = response[0].consents.map(
      (newestConsent) => {
        const consent = response[1].consents.find(
          (consent) => consent.name === newestConsent.name
        );

        if (!consent)
          // NEW CONSENT
          return {
            Accepted: false,
            ConsentName: newestConsent.name,
            ConsentVersion: newestConsent.version,
            ConsentRequired: newestConsent.is_required,
            ConsentContentUrl: newestConsent.value,
            ConsentContent: newestConsent.label,
            NeedUpdate: newestConsent.is_required,
          };
        else
          return {
            Accepted: consent.state === UserConsentsState.Accepted,
            ConsentName: newestConsent.name,
            ConsentVersion: newestConsent.version,
            ConsentDateOfAccept: consent.date,
            ConsentRequired: newestConsent.is_required,
            ConsentContentUrl: newestConsent.value,
            ConsentContent: newestConsent.label,
            NeedUpdate: consent.is_update_needed,
          };
      }
    );
    return consents.reduce((arr: ICommonUserConsentModel[], consent) => {
      const [consentLocale, consentName] = consent.ConsentName.split("_");
      if (consentLocale.toUpperCase() === userLocale.toUpperCase())
        arr.push({ ...consent, ConsentName: consentName });
      return arr;
    }, []);
  }

  static toSubscriptionModel(data: ISubscriptionModel): IUserSubscriptionModel {
    const UPODI_MONTHLY_ID = 100;
    const FRISBII_MONTHLY_ID = 155;

    const product = data.product;
    const partner = data.partner;
    const discount = data.discount;
    const switchTo = data.switch_to_subscription;

    const isActive = data.status === UserSubscriptionStatus.ACTIVE;
    const isPaused = data.status === UserSubscriptionStatus.HOLD;
    const isCancelled = data.status === UserSubscriptionStatus.CANCELLED;
    const isInDunning = data.status === UserSubscriptionStatus.IN_DUNNING;
    const isTrial = data.status === UserSubscriptionStatus.TRIAL;
    const isPendingSwitch =
      switchTo?.status === UserSubscriptionStatus.PENDING_SWITCH;

    const isEligibleForSwitch = product.switch_group !== null;
    const hasBeenChangedAlready = !!switchTo;

    const canBeCancelled = !!data.next_payment || isInDunning;
    const canBeChanged =
      canBeCancelled &&
      isEligibleForSwitch &&
      !hasBeenChangedAlready &&
      !isInDunning;
    // TODO Base this on data provided by API instead of hardcoded ids once
    // this functionality is reimplemented
    // const canBePaused = [UPODI_MONTHLY_ID, FRISBII_MONTHLY_ID].includes(
    //   product.id
    // );
    // FIXME API does not allow to pause subscriptions after migration to new payment provider.
    const canBePaused = false;

    const subscription: IUserSubscriptionModel = {
      Id: data.id,
      OfferId: product.id,
      OfferTitle: product.name,
      IsActive: isActive || isInDunning || isPendingSwitch || isTrial,
      IsCancelled: isCancelled,
      IsPaused: isPaused,
      IsExpired: new Date(data.expires_at).getTime() < Date.now(),
      CanBeCancelled: canBeCancelled,
      CanBeChanged: canBeChanged,
      CanBePaused: canBePaused,
      ExpirationDate: data.expires_at,
      ExpirationDateLabel: TimeHelper.format(
        data.expires_at,
        "DD. MMMM YYYY"
      ).toLowerCase(),
      NextPaymentPrice: switchTo
        ? product.price
          ? +product.price
          : undefined
        : data.next_payment?.payment_price,
      NextPaymentCurrency: product.currency,
      DiscountPeriod: discount?.fixed_period,
      DiscountAmount: discount?.amount,
      Period: product?.product_period.unit,
      Partner: partner
        ? {
            Id: partner.id,
            Name: partner.name || "",
          }
        : undefined,
      SwitchToOffer: switchTo
        ? {
            Id: switchTo.product.id,
            Name: switchTo.product.name,
            Period: switchTo.product.product_period.unit,
            Price: switchTo.product.price ? +switchTo.product.price : 0,
            Status: switchTo.status,
          }
        : undefined,
      Status: data.status,
    };

    return JSON.parse(JSON.stringify(subscription));
  }

  static toPaymentOfferModel(data: IOfferResponseModel): IPaymentOfferModel {
    return {
      Id: data.id,
      Title: data.title,
      Price: +data.price,
      Period: data.period,
      Currency: data.currency,
      IsTaxIncluded: data.is_tax_included,
      Handle: data.handle,
      SavingsPrice: data.overrides?.price,
      Default: data.period === PaymentPeriod.Year,
    };
  }

  static toProductModel(data: IProductResponseModel): IProductModel {
    return {
      Id: data.id,
      Price: +data.price,
      Period: data.period,
      Currency: data.currency,
      Default: data.period === PaymentPeriod.Year,
    };
  }

  static toPaymentOrderModel(
    data: IPaymentOrderResponseModel
  ): IPaymentOrderModel {
    return {
      CheckoutUrl: data.checkout_url,
      OrderId: data.order_id,
    };
  }

  static toPaymentDetailsModel(
    data: IPaymentDetailsResponseModel
  ): IPaymentDetailsModel {
    return {
      Id: data.id,
      CardType: data.card?.card_type,
      PaymentMethod: data.payment_type,
      ExpirationDate: data.card?.exp_date,
      MaskedCard: data.card?.masked_card,
    };
  }

  static toInvoiceModel(
    data: IPaymentInvoiceResponseModel
  ): IPaymentInvoiceModel {
    return {
      TransationId: data.transaction_id,
      OfferId: data.offer_id,
      OfferTitle: data.offer_title,
      OfferPeriod: data.offer_period,
      CustomerEmail: data.customer_email,
      CustomerCountry: data.customer_country,
      CustomerCurrency: data.customer_currency,
      TransationPrice: data.transaction_price_incl_tax,
      PaymentSuccess: data.payment_status === "paid",
      TransationDate: TimeHelper.format(
        data.transaction_date * 1000,
        "DD/MM-YY"
      ).toLowerCase(),
      PeriodFrom: TimeHelper.format(
        data.period_from * 1000,
        "DD/MM-YY"
      ).toLowerCase(),
      PeriodTo: TimeHelper.format(
        data.period_to * 1000,
        "DD/MM-YY"
      ).toLowerCase(),
      PaymentMethod: data.payment_method,
    };
  }

  static toOfferSwitchModel(
    data: IOfferSwitchResponseModel
  ): IOfferSwitchModel {
    return {
      Currency: data.currency,
      NextPaymentPrice: data.price,
      Period: data.period,
      SavingsPrice: data.annual_price,
      Title: data.name,
      Price: data.price,
      ToOfferId: data.id,
    };
  }

  static toValidCouponModel(
    data: IValidateCouponResponseModel
  ): IValidCouponModel {
    return {
      Coupon: {
        Code: data.coupon.code,
        ValidUntil: data.coupon.valid_until,
        RedemptionStrategy: data.coupon.redemption_strategy,
      },
      Discount: {
        Currency: data.discount.currency,
        Handle: data.discount.handle,
        FixedPeriodUnit: data.discount.fixed_period_unit,
        FixedPeriod: data.discount.fixed_period,
        FixedCount: data.discount.fixed_count,
        Percentage: data.discount.percentage,
        Amount: data.discount.amount,
        Forever:
          data.discount.fixed_period === null &&
          data.discount.fixed_count === null,
        IsFullDiscount: data.discount.percentage === 100,
      },
      Offers: data.offers.map((offer) => ({
        Id: `${offer.id}`,
        Handle: offer.handle,
        Name: offer.name,
        DiscountAmount: offer.discount_amount,
        DiscountedPrice: offer.discounted_price,
        TaxValue: offer.tax_value,
        Price: parseFloat(offer.price),
        Currency: offer.currency,
        Period: offer.period,
        RequiredPaymentDetails: offer.required_payment_details,
      })),
    };
  }

  static toOrderStatusModel(
    data: IOrderStatusResponseModel
  ): IOrderStatusModel {
    const orderStatus: IOrderStatusModel = {
      Order: {
        Status: data.order.status,
        RedbeeStatus: data.order.redbee_status,
        AgillicStatus: data.order.agillic_status,
      },
      Subscription: null,
    };

    if (data.error) {
      orderStatus.Error = {
        Message: data.error.message,
        Stack: data.error.stack,
      };
    }

    if (data.subscription) {
      orderStatus.Subscription = {
        Id: data.subscription.id,
        Status: data.subscription.status,
      };
    }

    if (data.reepay_payment_method) {
      orderStatus.PaymentMethod = {
        Id: data.reepay_payment_method.id,
        PaymentType: data.reepay_payment_method.payment_type,
        CardType: data.reepay_payment_method.card.card_type,
        MaskedCard: data.reepay_payment_method.card.masked_card,
        ExpirationDate: data.reepay_payment_method.card.exp_date,
      };
    }

    return orderStatus;
  }

  static toLocaleModel(locale: ILocaleModel): IInternalLocaleModel {
    const country = locale.content_country ?? locale.country;
    const primaryLanguage = locale.languages?.[0];

    return {
      Country: {
        Code: country.code,
        Name: country.name,
      },
      Language: {
        Code: primaryLanguage.code,
        Name: primaryLanguage.name,
      },
      Languages: locale.languages?.map((language) => ({
        Code: language.code,
        Name: language.name,
      })),
      Locale: `${primaryLanguage?.code}_${country.code}`,
    };
  }
}
